import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback, useEffect, Suspense } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Palette,
  ChevronRight,
  X,
  ArrowLeft,
  MoreHorizontal,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { myappApi } from "@/services/api/myapp";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import type { AppDetail, AppCreate } from "@/types/myapp";
import { ObjectGroupAccessDialog } from "@/components/portal/groups/ObjectGroupAccessDialog";
import { useGroupAccessDeepLink } from "@/components/portal/groups/useGroupAccessDeepLink";
import { BulkGroupAccessDialog } from "@/components/portal/groups/BulkGroupAccessDialog";
import { groupsApi } from "@/services/api/groups";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/portal/myapp/apps")({
  component: AppsPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { groupAccess?: string; groupAccessName?: string } => ({
    groupAccess:
      typeof search.groupAccess === "string" && search.groupAccess.length > 0
        ? search.groupAccess
        : undefined,
    groupAccessName:
      typeof search.groupAccessName === "string" && search.groupAccessName.length > 0
        ? search.groupAccessName
        : undefined,
  }),
});

function AppsPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      }
    >
      <AppsPageContent />
    </Suspense>
  );
}

function AppsPageContent() {
  const { token } = useAuth();
  const { selectedOrganisation, loading: orgLoading } = usePortalData();
  const { can, bypassesGroups } = usePermissions();
  const canManageApps = can("manage", "tables");

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Create/Edit dialog state
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<AppDetail | null>(null);
  const [appFormData, setAppFormData] = useState<AppCreate>({
    name: "",
    description: null,
    icon: null,
    colour: null,
  });
  const [savingApp, setSavingApp] = useState(false);

  // Delete dialog state
  const [deleteAppDialogOpen, setDeleteAppDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<AppDetail | null>(null);
  const [deletingApp, setDeletingApp] = useState(false);

  // Group access dialog state
  const [groupAccessTarget, setGroupAccessTarget] = useState<{ id: string; name: string } | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGroupAccessOpen, setBulkGroupAccessOpen] = useState(false);

  // Group permissions per app (fetched in bulk)
  const [appPermissions, setAppPermissions] = useState<
    Map<string, { group_name: string; role: string }[]>
  >(new Map());

  // Error state
  const [actionError, setActionError] = useState<string | null>(null);

  // Server-side paginated data fetch — sorting is a server param so it
  // orders the whole dataset, not just the fetched page.
  const fetchApps = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<AppDetail>> => {
      if (orgLoading || !selectedOrganisation || !token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await myappApi.apps.list(token, {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: searchTerm || undefined,
        sortBy: sort?.key as "name" | "created_at" | "table_count" | undefined,
        sortOrder: sort?.direction,
      });
      return {
        data: response.items,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, selectedOrganisation, orgLoading, searchTerm],
  );

  const {
    data: apps,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<AppDetail>({
    fetchFn: fetchApps,
    initialPageSize: 25,
    dependencies: [token, selectedOrganisation, searchTerm],
    initialSort: { key: "name", direction: "asc" },
  });

  const displayApps = apps;

  // Deep link from the group Object Permissions tab: ?groupAccess=<app id>
  const { groupAccess, groupAccessName } = Route.useSearch();
  useGroupAccessDeepLink({
    groupAccess,
    groupAccessName,
    // Server-paginated at 25, so this is normally false and the link's own name is used.
    // `!searchTerm` too: `totalCount` narrows to the search. An earlier comment here claimed
    // no search box is mounted while the link is unconsumed — true of the error window,
    // FALSE of the rbacResolving window, where the page renders fully and the box is live.
    // AND `bypassesGroups`: this list is filtered server-side by the caller's own object
    // permissions unless they bypass, so for a non-bypass caller "complete" still omits
    // objects that exist. Reporting those as absent is a false, unrecoverable not-found.
    listIsComplete: apps.length >= pagination.totalCount && !searchTerm && bypassesGroups,
    // `!error`: a failed fetch leaves `apps` empty and `totalCount` at 0, which would read
    // as a complete empty list and report a false "Object not found".
    ready: !isLoading && !error && !orgLoading && !!selectedOrganisation,
    canManageGroups: canManageApps,
    items: apps,
    getId: (app) => app.id,
    getName: (app) => app.name,
    onMatch: setGroupAccessTarget,
  });

  // Clear selection on data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, pagination.page]);

  // Selection helpers
  const allOnPageSelected = displayApps.length > 0 && displayApps.every((a) => selectedIds.has(a.id));
  const someOnPageSelected = displayApps.some((a) => selectedIds.has(a.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayApps.map((a) => a.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = displayApps.filter((a) => selectedIds.has(a.id)).map((a) => ({ id: a.id, name: a.name }));

  // Fetch all group permissions for apps (one bulk call)
  const orgId = selectedOrganisation?.organisationId;
  const fetchAppPermissions = useCallback(async () => {
    // cp answers by-object only for org_owner / org_admin / platform_admin (BYPASS_ROLES,
    // `_require_grant_visibility`); anyone else gets a 403 that trips Platform Health (web #1444).
    if (!token || !orgId || !bypassesGroups) return;
    try {
      const resp = await groupsApi.listObjectPermissionsByObject(token, orgId, {
        service_name: "myapp",
        object_type: "app",
      });
      const grouped = new Map<string, { group_name: string; role: string }[]>();
      for (const item of resp.items) {
        const existing = grouped.get(item.object_id) ?? [];
        existing.push({ group_name: item.group_name, role: item.role });
        grouped.set(item.object_id, existing);
      }
      setAppPermissions(grouped);
    } catch {
      // Non-critical
    }
  }, [token, orgId, bypassesGroups]);

  useEffect(() => {
    fetchAppPermissions();
  }, [fetchAppPermissions, apps]);

  // Handlers
  const handleCreateAppClick = () => {
    setEditingApp(null);
    setAppFormData({ name: "", description: null, icon: null, colour: null });
    setAppDialogOpen(true);
  };

  const handleEditAppClick = (app: AppDetail) => {
    setEditingApp(app);
    setAppFormData({
      name: app.name,
      description: app.description,
      icon: app.icon,
      colour: app.colour,
    });
    setAppDialogOpen(true);
  };

  const handleAppSubmit = async () => {
    if (!appFormData.name.trim() || !token) return;

    try {
      setSavingApp(true);
      if (editingApp) {
        await myappApi.apps.update(token, editingApp.id, appFormData);
      } else {
        await myappApi.apps.create(token, appFormData);
      }
      setAppDialogOpen(false);
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to save app");
    } finally {
      setSavingApp(false);
    }
  };

  const handleDeleteAppClick = (app: AppDetail) => {
    setAppToDelete(app);
    setDeleteAppDialogOpen(true);
  };

  const handleDeleteAppConfirm = async () => {
    if (!appToDelete || !token) return;

    try {
      setDeletingApp(true);
      await myappApi.apps.delete(token, appToDelete.id);
      setDeleteAppDialogOpen(false);
      setAppToDelete(null);
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to delete app");
    } finally {
      setDeletingApp(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader onCreateClick={handleCreateAppClick} canManage={canManageApps} />
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-destructive">{error}</p>
            <Button onClick={refresh} variant="outline">Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader onCreateClick={handleCreateAppClick} canManage={canManageApps} />

      {/* Content */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search apps..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Action error */}
        {actionError && (
          <div className="p-3 border border-destructive/50 bg-destructive/10 rounded-md flex items-center justify-between">
            <p className="text-sm text-destructive">{actionError}</p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setActionError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <span className="text-sm font-medium">
              {selectedIds.size} app{selectedIds.size !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              onClick={() => setBulkGroupAccessOpen(true)}
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Group Access
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayApps.length === 0 ? (
          <div className="text-center py-12">
            <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No apps match your search" : "No apps yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Create your first app to start grouping tables together."}
            </p>
            {!searchTerm && canManageApps && (
              <Button onClick={handleCreateAppClick}>
                <Plus className="h-4 w-4 mr-2" />
                Create App
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px] px-3">
                    <Checkbox
                      checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("table_count")}
                    onSort={() => requestSort("table_count")}
                  >
                    Tables
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => requestSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  <TableHead>Groups</TableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayApps.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="w-[40px] px-3">
                      <Checkbox
                        checked={selectedIds.has(app.id)}
                        onCheckedChange={() => toggleSelect(app.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {app.colour ? (
                          <div
                            className="w-4 h-4 rounded-full shrink-0 border"
                            style={{ backgroundColor: app.colour }}
                          />
                        ) : (
                          <Palette className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <div>
                          <Link
                            to="/portal/apps/$appSlug"
                            params={{ appSlug: app.slug || app.id }}
                            className="font-medium text-primary hover:underline"
                          >
                            {app.name}
                          </Link>
                          {app.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[400px]">
                              {app.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {app.table_count} {app.table_count === 1 ? "table" : "tables"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(app.created_at_utc)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <GroupBadges permissions={appPermissions.get(app.id)} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to="/portal/apps/$appSlug"
                              params={{ appSlug: app.slug || app.id }}
                            >
                              <ChevronRight className="h-4 w-4 mr-2" />
                              View App
                            </Link>
                          </DropdownMenuItem>
                          {canManageApps && (
                            <DropdownMenuItem onClick={() => handleEditAppClick(app)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canManageApps && (
                            <DropdownMenuItem onClick={() => setGroupAccessTarget({ id: app.id, name: app.name })}>
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              Group Access
                            </DropdownMenuItem>
                          )}
                          {canManageApps && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteAppClick(app)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit App Dialog */}
      <Dialog open={appDialogOpen} onOpenChange={setAppDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingApp ? "Edit App" : "Create App"}</DialogTitle>
            <DialogDescription>
              {editingApp
                ? "Update the app details."
                : "Create a new app to group related tables together."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="app-name">Name</Label>
              <Input
                id="app-name"
                placeholder="e.g., Sales, HR, Inventory"
                value={appFormData.name}
                onChange={(e) =>
                  setAppFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && appFormData.name.trim()) {
                    handleAppSubmit();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-description">Description (optional)</Label>
              <Textarea
                id="app-description"
                placeholder="What is this app for?"
                value={appFormData.description || ""}
                onChange={(e) =>
                  setAppFormData((prev) => ({
                    ...prev,
                    description: e.target.value || null,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-colour">Colour (optional)</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="app-colour"
                  type="color"
                  className="w-12 h-10 p-1 cursor-pointer"
                  value={appFormData.colour || "#6366f1"}
                  onChange={(e) =>
                    setAppFormData((prev) => ({
                      ...prev,
                      colour: e.target.value,
                    }))
                  }
                />
                <Input
                  value={appFormData.colour || ""}
                  placeholder="#6366f1"
                  className="flex-1"
                  onChange={(e) =>
                    setAppFormData((prev) => ({
                      ...prev,
                      colour: e.target.value || null,
                    }))
                  }
                />
                {appFormData.colour && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setAppFormData((prev) => ({ ...prev, colour: null }))
                    }
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAppDialogOpen(false)}
              disabled={savingApp}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAppSubmit}
              disabled={savingApp || !appFormData.name.trim()}
            >
              {savingApp
                ? "Saving..."
                : editingApp
                  ? "Save Changes"
                  : "Create App"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete App Confirmation */}
      <Dialog
        open={deleteAppDialogOpen}
        onOpenChange={setDeleteAppDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete App</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{appToDelete?.name}&quot;?
              Tables in this app will not be deleted, only the app grouping will
              be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteAppDialogOpen(false)}
              disabled={deletingApp}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAppConfirm}
              disabled={deletingApp}
            >
              {deletingApp ? "Deleting..." : "Delete App"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Access Dialog */}
      {groupAccessTarget && (
        <ObjectGroupAccessDialog
          serviceName="myapp"
          objectType="app"
          objectId={groupAccessTarget.id}
          objectName={groupAccessTarget.name}
          open={!!groupAccessTarget}
          onOpenChange={(open) => { if (!open) { setGroupAccessTarget(null); fetchAppPermissions(); } }}
        />
      )}

      {/* Bulk Group Access Dialog */}
      <BulkGroupAccessDialog
        serviceName="myapp"
        objectType="app"
        items={selectedItems}
        open={bulkGroupAccessOpen}
        onOpenChange={setBulkGroupAccessOpen}
        onComplete={() => { setSelectedIds(new Set()); fetchAppPermissions(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extracted sub-components
// ---------------------------------------------------------------------------

const MAX_VISIBLE_BADGES = 2;

function GroupBadges({ permissions }: { permissions?: { group_name: string; role: string }[] }) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const visible = permissions.slice(0, MAX_VISIBLE_BADGES);
  const remaining = permissions.length - MAX_VISIBLE_BADGES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-1">
        {visible.map((p, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs cursor-default">
                {p.group_name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{p.role}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs cursor-default">
                +{remaining}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {permissions.slice(MAX_VISIBLE_BADGES).map((p) => `${p.group_name} (${p.role})`).join(", ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

function PageHeader({ onCreateClick, canManage }: { onCreateClick: () => void; canManage: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-4">
        <Link
          to="/portal/myapp"
          className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to myApp
        </Link>
      </div>
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">Apps</h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-600">
            Group related tables into apps to organise them logically.
          </p>
        </div>
        {canManage && (
          <Button onClick={onCreateClick} className="rounded-full">
            <Plus className="h-4 w-4 mr-2" />
            New App
          </Button>
        )}
      </div>
    </div>
  );
}
